import styles from './ccf-copilot.module.scss';
import { useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AdaptiveCard } from 'adaptivecards-react';
import * as ACData from 'adaptivecards-templating';
import ccfCopilotCardStyles from './ccf-live-copilot.styles';
import {
  activeContactSelector,
  getActiveContactID,
  getActiveInteractionID,
  selectInboxCollapsedState,
} from '../../ccf-inbox/ccf-inbox.slice';
import {
  CopilotElement,
  getAdaptiveCards,
  getCopilotConfig,
  getIsRequestProcessing,
  selectRunningSummary,
  getIsJourneySummaryExpanded,
  getJourneySummaryAdaptiveCard,
  setIsRequestProcessing,
  getisFeedbackSaved,
  setCopilotToInitialState,
  setFinalSummary,
  getLatestAudioTranscript,
} from './ccf-copilot-chat/+state/ccf-live-copilot.slice';
import {
  CcfCard,
  CcfDancingDotsIcon,
  CcfImageWithZoom,
  CcfTypography,
  CcfSnackBar,
  CcfAlert,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { CcfBox } from '@nice-ccf/ui-controls';
import CcfAgentCopilotRunningSummary from './ccf-agent-copilot-running-summary';
import CcfAgentCopilotSearchContainer from './ccf-agent-copilot-search-container';
import CcfEmptyCopilotState from './ccf-empty-copilot-state';
import {
  ActionMetaData,
  CardActions,
  CardData,
  CcfAgentCopilotContainerFunctions,
  ContainerId,
  actionsForOpenContainer,
} from './ccf-live-copilot-helper';
import {
  handleAgentTransferSummary,
  handleAutopilotSummary,
  handleJourneySummaryData,
  handleSubmitIntentMessages,
} from './ccf-copilot-util';
import {
  updateCopilotAppSpaceArticle,
  resolveDispositionForInteraction,
  updateFlowMsgIndex,
} from '@nice-ccf/ui-components';
import {
  DigitalChannelStatus,
  DigitalChannelType,
  MediaType,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import { API_BASE_URL, JOURNEY_SUMMARY } from './ccf-copilot-api';

/**
 * Converts interactions to a formatted string.
 * @param interactions - The interactions to be formatted.
 * @returns The formatted string.
 * @example convertToFormattedString(interactions)
 */
const convertToFormattedString = (interactions: any) => {
  let formattedString = '';

  interactions?.forEach((interaction: any) => {
    formattedString += `"${interaction?.InteractionType}",`;
    formattedString += `"${interaction?.InteractionReason}",`;
    formattedString += `"${interaction?.InteractionDateTime}",`;
    formattedString += `"${interaction?.InteractionSentiment}",`;
    formattedString += `"${interaction?.IsResolved}",`;
    formattedString += `"${interaction?.InteractionOrigin}",`;
    formattedString += `"${interaction?.StepsTakenDuringCall}",`;
    formattedString += `"${interaction?.InteractionDurationInSeconds}",`;
    formattedString += `"${interaction?.CustomerName}",`;
    // eslint-disable-next-line no-useless-escape
    formattedString += `"${interaction?.InteractionSummary}\"\n`;
  });
  return formattedString;
};

/**
 * Extracts time from interaction date time.
 * @param interactions - The interactions to be formatted.
 * @returns The formatted string.
 * @example extractTimeFromInteractionDateTime(interactions)
 */
const extractTimeFromInteractionDateTime = (perviousInteractionData: any) => {
  const modifiedData: any = perviousInteractionData.map((interaction: any) => {
    const interactionDateTime: Date = new Date(interaction.InteractionDateTime);
    const interactionTime: string = interactionDateTime
      .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      .replace(/(\d+:\d+):\d+/, '$1');
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    const interactionDate: string = interactionDateTime.toLocaleDateString(
      undefined,
      options
    );
    return {
      ...interaction,
      InteractionTime: interactionTime,
      InteractionDate: interactionDate,
    };
  });
  return modifiedData;
};

/* eslint-disable-next-line */
export interface CcfLiveCopilotProps {}

const useStyles = makeStyles({
  sendInput: {
    '&.MuiOutlinedInput-input': {
      color: '#637A87',
      border: '0',
      outline: '0',
      fontSize: '14px',
    },
  },
});

/**
 * CcfLiveCopilot - used to display Copilot tab in App Space
 * @param props -?-CcfLiveCopilotProps
 * @example <CcfLiveCopilot />
 */
export function CcfLiveCopilot(props: CcfLiveCopilotProps) {
  const theme = useTheme();
  const activeContactId = useSelector(getActiveContactID);
  const dispatch = useDispatch();
  const activeContact = useSelector(activeContactSelector);
  const perviousInteractionData =
    activeContact?.perviousInteractionJsonData ?? [];
  const copilotConfigurations = useSelector(getCopilotConfig);
  const [previousInteractionSummary, setPreviousInteractionSummary] =
    useState('');
  const isInboxCollapsed = useSelector(selectInboxCollapsedState);
  const isRequestProcessing = useSelector(getIsRequestProcessing);
  const [isResponseLoading, setResponseLoading] = useState(isRequestProcessing);
  const cardStyles = ccfCopilotCardStyles(theme);
  const isLiveCopilotEnabled = activeContact?.copilotSource === 'Live LLM';
  const audioTranscript = useSelector(getLatestAudioTranscript);
  const hostConfig = {
    fontFamily: theme.typography.fontFamily,
    separator: {
      lineThickness: 1,
      lineColor: theme?.palette?.background?.noteInput,
    },
    supportsInteractivity: true,
    containerStyles: {
      default: {
        foregroundColors: {
          default: {
            default: theme?.palette?.secondary?.main,
            subtle: theme?.palette?.secondary?.main,
          },
          dark: {
            default: theme?.palette?.text?.contrastText,
          },
        },
      },
    },
  };
  const isChannelVoice = activeContact?.media === 'Voice' ? true : false;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const existingAdaptiveCards = useSelector(
    getAdaptiveCards(activeContactId || '')
  );
  //const existingAdaptiveCards = useSelector(getAdaptiveCards('201251606723'));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const rtSummary = useSelector(
    selectRunningSummary(activeContactId || '')
  ) as CopilotElement;
  //const rtSummary = (useSelector(selectRunningSummary('201251606723')) as CopilotElement);
  const rtSummaryContent = rtSummary?.content
    ? JSON.parse(rtSummary?.content as unknown as string)
    : null;
  const autopilotSummary = activeContact?.autopilotSummary ?? '';
  const transferSummary = activeContact?.transferSummary ?? '';
  const userReplyChatData = activeContact?.customerChatReplyJsonData ?? [];
  //const userReplyChatData = useSelector(getCustomerChatReplyJsonData(activeContact?.id))?? [];
  const intents = activeContact?.intents ?? [];
  const lastindexVal = activeContact?.customerChatReplyLastindexVal ?? 0;
  const activeInteractionID: string = useSelector(getActiveInteractionID) || '';

  const isFeedbackSaved =
    useSelector(getisFeedbackSaved(activeContactId || '')) || false;
  const [showToast, setShowToast] = useState(isFeedbackSaved);
  const [translate] = useTranslator();

  useEffect(() => {
    if (isFeedbackSaved === true) {
      setShowToast(true);
      setTimeout(() => {
        dispatch(setCopilotToInitialState());
        if (
          activeContact?.channel === 'Chat' &&
          activeContact?.copilotAssist === 'Yes'
        ) {
          dispatch(updateCopilotAppSpaceArticle([]));
        }
        dispatch(
          resolveDispositionForInteraction({
            id: activeContact?.id,
          })
        );
        // if (activeContact?.media === MediaType.VOICE) {
        // dispatch(
        //   updateCallStatus({
        //     id: activeContact.id,
        //     contactStatus: VoiceContactStatus.RESOLVED,
        //   })
        // );
        // } else {
        //activeContact &&
        // dispatch(
        //   updateCallStatus({
        //     id: activeContact.id,
        //     contactStatus: DigitalChannelStatus.RESOLVED,
        //   })
        // );
        //}
        const contactHistoryLocalStorage = JSON.parse(
          localStorage.getItem('contactHistoryLS') || '[]'
        );
        if (contactHistoryLocalStorage.length > 0) {
          const newContactforContactHistory = {
            id: 40555,
            time: new Date().toISOString(),
            customer: `${[
              activeContact?.customer?.firstName,
              activeContact?.customer?.middleName,
              activeContact?.customer?.lastName,
            ]
              .filter(Boolean)
              .join(' ')}`,
            status: activeContact?.contactStatus,
            skill: activeContact?.channel,
            disposition: activeContact?.contactStatus,
            channel: 'Usha-Routing-Queue',
          };
          const updatedContactHistoryArray = JSON.parse(
            JSON.stringify(contactHistoryLocalStorage)
          );
          updatedContactHistoryArray.unshift(newContactforContactHistory);
          localStorage.setItem(
            'contactHistoryLS',
            JSON.stringify(updatedContactHistoryArray)
          );
        }

        if (activeContact?.channel === DigitalChannelType.CHAT) {
          dispatch(updateFlowMsgIndex(0));
        }
        if (activeContact?.copilotSource === 'Live LLM') {
          setTimeout(() => {
            dispatch(
              setFinalSummary({
                summary: '',
                resolved: false,
                intents: '',
                actions: '',
                outcomes: '',
                entities: '',
              })
            );
          }, 2000);
        }
      }, 3000);
    }
  }, [isFeedbackSaved]);

  useEffect(() => {
    setResponseLoading(isRequestProcessing);
  }, [isRequestProcessing]);

  useEffect(() => {
    let timeoutId: any = null;
    if (isResponseLoading) {
      timeoutId = setTimeout(() => {
        setResponseLoading(false);
      }, 20000);
    }
    return () => clearTimeout(timeoutId);
  }, [isResponseLoading]);

  const [imageExpand, setimageExpand] = useState({ url: '', altText: '' });
  const context: ACData.IEvaluationContext = {
    $root: {},
  };
  const isSmView = false;

  const isJourneySummaryExpanded = useSelector(
    getIsJourneySummaryExpanded(activeContactId || '')
  );
  const journeySummaryCard = useSelector(
    getJourneySummaryAdaptiveCard(activeContactId || '')
  );

  useEffect(() => {
    setResponseLoading(isRequestProcessing);
    if (isRequestProcessing) {
      const timeoutId = setTimeout(() => {
        setResponseLoading(false);
      }, 20000);
      return () => clearTimeout(timeoutId);
    }
    return;
  }, [isRequestProcessing]);

  useEffect(() => {
    if (autopilotSummary !== '') {
      handleAutopilotSummary(
        activeContact,
        autopilotSummary,
        dispatch,
        translate
      );
    }
    if (transferSummary !== '') {
      handleAgentTransferSummary(
        activeContact,
        transferSummary,
        dispatch,
        translate
      );
    }
  }, [autopilotSummary, activeContact?.id]);

  useEffect(() => {
    // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
    const fetchPreviousInteractionSummary = async (formatedString: string) => {
      try {
        const response = await fetch(API_BASE_URL + JOURNEY_SUMMARY, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              'Basic YWdlbnRjb3BpbG90YWRtaW46bmljZTJoYXZleW91IzIwMjQ=',
          },
          body: JSON.stringify({
            Chats: `The data below describes previous interactions of this customer with us. Summarize this data for an agent so they know what to expect in this interaction includes dates. Reply in less than 40 words. The name of the customer is ${activeContact?.customer?.firstName}\nInteractionType,InteractionReason,InteractionDateTime,InteractionSentiment,IsResolved,InteractionOrigin,StepsTakenDuringCall,InteractionDurationInSeconds,CustomerName,InteractionSummary\n${formatedString}`,
          }),
        });
        if (!response.ok) {
          throw new Error('Failed to fetch previous interaction');
        }
        const data = await response.json();
        setPreviousInteractionSummary(
          data.completion ??
            `No information found for success ${[
              activeContact?.customer?.firstName,
              activeContact?.customer?.middleName,
              activeContact?.customer?.lastName,
            ]
              .filter(Boolean)
              .join(' ')} - ${copilotConfigurations?.path ?? 'custom'}`
        );
        const updatedPerviousInteractionData =
          extractTimeFromInteractionDateTime(perviousInteractionData);
        handleJourneySummaryData(
          activeContact,
          data.completion,
          updatedPerviousInteractionData,
          dispatch
        );
      } catch (error) {
        console.error('Error fetching previous interaction:', error);
        setPreviousInteractionSummary(
          `No information found for error ${[
            activeContact?.customer?.firstName,
            activeContact?.customer?.middleName,
            activeContact?.customer?.lastName,
          ]
            .filter(Boolean)
            .join(' ')} - ${copilotConfigurations?.path ?? 'custom'}`
        );
      }
    };
    if (perviousInteractionData.length > 0) {
      const formatedString = convertToFormattedString(perviousInteractionData);
      fetchPreviousInteractionSummary(formatedString);
    }
  }, [activeContact?.id]);

  /**
   * Function to handle on click event for expand image
   * @param imgUrl - string
   * @param imgAltText - string
   * @example handleExpandImageClick(imgUrl, imgAltText)
   */
  const handleExpandImageClick = (imgUrl: string, imgAltText: string) => {
    setimageExpand({ url: imgUrl, altText: imgAltText });
  };

  /**
   * Used to close the snackbar
   * @param event -React.SyntheticEvent
   * @param reason -string
   * @example handleExpandImageClick(imgUrl, imgAltText)
   */
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowToast(false);
  };

  /**
   * Function to handle on click event for actions
   * @param action - ActionMetaData
   * @param cardItem - CardData
   * @param cardId - number
   * @example handleOnClick(action, cardItem, cardId)
   */
  const handleOnClick = async (
    action: ActionMetaData,
    cardItem: CardData,
    cardId: number
  ) => {
    const caseId = activeContactId || '';
    //const  caseId = '201251606723';
    const actionName = action.data?.name;
    if (actionName === CardActions.EXPAND_IMAGE) {
      setIsModalOpen(true);
    }
    let containerIndex = 3;
    if (actionsForOpenContainer.includes(actionName)) {
      let containerId;

      switch (actionName) {
        case CardActions.OPEN_LINKS_CONTAINER:
          containerId = ContainerId.KB_LINKS;
          containerIndex = 1;
          break;
        case CardActions.OPEN_IMAGES_CONTAINER:
          containerId = ContainerId.KB_IMAGES;
          containerIndex = 2;
          break;
        case CardActions.OPEN_PROCESS_STEPS_CONTAINER:
          containerId = ContainerId.KB_PROCESS_STEPS;
          containerIndex = 3;
          break;
        default:
          break;
      }

      containerId &&
        CcfAgentCopilotContainerFunctions.handleContainerOpen(
          cardItem.body[containerIndex],
          cardId,
          containerId,
          caseId,
          dispatch
        );
    }

    // if(actionName === CardActions.OPEN_FEEDBACK_TOPICS_CONTAINER){
    //   const index = +(action.data?.index ?? 0) + 1;
    //   const itemsContainer = cardItem.body[index].items as CardElement[];
    //   const feedbackConversationTopicsContainer = CcfAgentCopilotContainerFunctions.getContainerById( cardItem.body[index].items as CardElement[], ContainerId.FEEDBACK_COMMENTS_CONTAINER );
    //   await CcfAgentCopilotContainerFunctions.handleFeedbackConversationTopicsOpen(
    //     feedbackConversationTopicsContainer,
    //     cardId,
    //     ContainerId.FEEDBACK_COMMENTS_CONTAINER,
    //     caseId,
    //     dispatch,
    //     cardItem.body[index],
    //     index
    //   );

    //   await CcfAgentCopilotContainerFunctions.handleIconChange(itemsContainer[0], action, cardId, caseId, 'openFeedbackTopicsContainer', dispatch);
    // }
    if (actionName === CardActions.OPEN_WORKFLOW_CONTAINER) {
      const workflowQuestionsContainer =
        CcfAgentCopilotContainerFunctions.getContainerById(
          cardItem.body,
          ContainerId.WORKFLOW_QUESTIONS_CONTAINER
        );
      await CcfAgentCopilotContainerFunctions.handleContainerOpen(
        workflowQuestionsContainer,
        cardId,
        ContainerId.WORKFLOW_QUESTIONS_CONTAINER,
        caseId,
        dispatch
      );
      const workflowButtonsContainer =
        CcfAgentCopilotContainerFunctions.getContainerById(
          cardItem.body,
          ContainerId.WORKFLOW_BUTTONS_CONTAINER
        );
      await CcfAgentCopilotContainerFunctions.handleContainerOpen(
        workflowButtonsContainer,
        cardId,
        ContainerId.WORKFLOW_BUTTONS_CONTAINER,
        caseId,
        dispatch
      );
    }

    if (actionName === CardActions.SUBMIT_INTENTS) {
      await handleSubmitIntentMessages(
        caseId,
        cardId,
        action.data?.text,
        dispatch,
        translate
      );
    }
    if (actionName !== CardActions.HANDLE_INTENTS) {
      await CcfAgentCopilotContainerFunctions.handleClickActions(
        { action, cardItem, caseId, isSmView, cardId, activeInteractionID },
        CcfAgentCopilotContainerFunctions.handleIconChange,
        handleExpandImageClick,
        dispatch,
        userReplyChatData?.[0]?.agentName?.split(' ')?.[0]
      );
    }
    if (actionName === CardActions.HANDLE_INTENTS) {
      // Use the intent of the card that was actually clicked (matched by
      // title), not the shared activeContact.intents which only holds whichever
      // Task Assist was rendered last. Falls back to intents when not found.
      const clickedTitle = action.data?.text;
      const clickedIntent =
        userReplyChatData?.find(
          (item: any) =>
            item?.recordType === 'intentAndAssistance' &&
            item?.message?.[0]?.title === clickedTitle
        )?.message ?? intents;

      await CcfAgentCopilotContainerFunctions.handleInsertingIntentMessages(
        caseId,
        clickedIntent,
        userReplyChatData,
        userReplyChatData?.[0]?.agentName?.split(' ')?.[0],
        activeContact?.customer.firstName,
        lastindexVal,
        activeInteractionID,
        dispatch
      );
      const taskInterval =
        clickedIntent[0]?.tasks[0]?.buttons?.[0]?.time ?? 3000;
      const orderLength = clickedIntent[0].tasks?.length * 2;
      const interval = taskInterval * orderLength;
      setTimeout(() => {
        handleSubmitIntentMessages(
          caseId,
          cardId,
          clickedIntent[0].title,
          dispatch,
          translate
        );
      }, interval);
    }
  };

  /**
   * Function to close modal
   * @example closeModal()
   */
  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    // Scroll to the bottom of the container when new cards are added
    if (containerRef.current) {
      setTimeout(() => {
        containerRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 0);
      //dispatch(setIsRequestProcessing(false));
    }
  }, [existingAdaptiveCards.length]);

  return isJourneySummaryExpanded ? (
    <Box
      sx={
        isChannelVoice
          ? cardStyles.journeySummaryContainerVoice
          : cardStyles.journeySummaryContainer
      }
    >
      <CcfBox sx={cardStyles.cardsContainer}>
        {journeySummaryCard.map((card, cardId) => {
          const template = new ACData.Template(card.content);
          const copilotCard = template.expand(context);
          return (
            <AdaptiveCard
              key={`ad-${cardId}`}
              style={{ fontFamily: theme.typography.fontFamily }}
              payload={copilotCard}
              hostConfig={hostConfig}
              onExecuteAction={(action: any) =>
                handleOnClick(action, copilotCard, cardId)
              }
            />
          );
        })}
      </CcfBox>
    </Box>
  ) : (
    <>
      <Box sx={cardStyles.copilotContainer}>
        {rtSummary && (
          <CcfAgentCopilotRunningSummary summary={rtSummaryContent.summary} />
        )}
        <CcfBox sx={cardStyles.cardsContainer}>
          {existingAdaptiveCards.length === 0 ? (
            <CcfEmptyCopilotState />
          ) : (
            <CcfCard sx={cardStyles.copilotCard} data-testid="list-item">
              <div ref={containerRef}>
                {existingAdaptiveCards.map((card, cardId) => {
                  let updatedCopilotCard = { ...card.content };
                  try {
                    updatedCopilotCard = JSON.stringify(card.content).replace(
                      /adp_\w+/g,
                      (match) => {
                        const value = translate(match);
                        return value ? value : match;
                      }
                    );
                    updatedCopilotCard = JSON.parse(updatedCopilotCard);
                  } catch (e) {
                    console.log('[error] ------->', e);
                  }
                  const template = new ACData.Template(updatedCopilotCard);
                  const copilotCard = template.expand(context);
                  console.log(
                    new Date().toISOString(),
                    `card rendered ${card.objectId}`
                  );
                  return (
                    <Box
                      sx={
                        isChannelVoice
                          ? cardStyles.copilotContentVoice
                          : cardStyles.copilotContent
                      }
                      key={cardId}
                    >
                      <AdaptiveCard
                        key={`ad-${cardId}`}
                        style={{ fontFamily: theme.typography.fontFamily }}
                        payload={copilotCard}
                        hostConfig={hostConfig}
                        onExecuteAction={(action: any) =>
                          handleOnClick(action, copilotCard, cardId)
                        }
                      />
                      <CcfImageWithZoom
                        isModalOpen={isModalOpen}
                        closeModal={closeModal}
                        isInboxCollapsed={isInboxCollapsed}
                        imgUrl={imageExpand.url}
                        imgAltText={imageExpand.altText}
                      />
                    </Box>
                  );
                })}
              </div>
            </CcfCard>
          )}
        </CcfBox>
        {isResponseLoading && (
          <CcfBox style={cardStyles.copilotInteractionAnimation}>
            <CcfDancingDotsIcon style={cardStyles.dancingDotsIcon} />
          </CcfBox>
        )}
        <CcfTypography sx={cardStyles.transcriptionText}>
          {audioTranscript}
        </CcfTypography>
      </Box>
      <CcfBox>
        <CcfSnackBar
          open={showToast}
          autoHideDuration={3000}
          onClose={() => handleClose()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <div>
            <CcfAlert closeAlert={handleClose} severity="success">
              {`Feedback saved successfully!`}
            </CcfAlert>
          </div>
        </CcfSnackBar>
      </CcfBox>
      {<CcfAgentCopilotSearchContainer />}
    </>
  );
}

export default CcfLiveCopilot;
